import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BomPartsImport } from 'bom/components';
import { ImportType } from 'bom/enums';
import { BomPartsImportProvider } from 'bom/states';
import { IImportPartsStatus } from 'bom/types';
import { ICliJob } from 'cli/types';
import { FileUploadType, NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { useImportQuoteActions, useQuotePartActions } from 'quotes/hooks';
import { ICustomerQuote, ICustomerQuotePart } from 'quotes/types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  quote: ICustomerQuote;
  type: ImportType;
  fileId?: string;
  onSubmitSuccess?: () => void;
}

export const QuotePartsImportBom = ({
  isOpen,
  onClose,
  quote,
  type,
  fileId,
  onSubmitSuccess,
}: IProps) => {
  const { t } = useTranslation();

  const { updateRows } = useAgGridClientTableContext();
  const { showLoadingNotification, showSuccessNotification } = useNotificationContext();

  const { handleCliJob } = useCliJobHandler();
  const { createQuoteImport } = useImportQuoteActions();
  const { resetPricing } = useQuotePartActions({
    notificationPosition: NotificationPosition.TOP,
  });

  const createImportPayload = useMemo(() => ({ quote: quote?.id }), [quote?.id]);

  const calculatePricingAction = async (importData: IImportPartsStatus) => {
    const data = {
      quoteParts: importData.partData.map((part) => part.id) || [],
      pricingTypes: ['cost', 'price'],
      omitCustomCost: 1,
      omitCustomPrice: 1,
      autoSetShipmentTypeAndLocation: 1,
      autoSetCompanyManufacturer: 1,
    };

    setTimeout(() => {
      showLoadingNotification(
        { message: t('messageCalculatePricingInProgress') },
        NotificationPosition.TOP
      );

      handleCliJob(resetPricing, data, {
        onCliJobFinished: (cliJob: ICliJob) => {
          const quoteParts = cliJob.result as ICustomerQuotePart[];

          updateRows({ update: quoteParts }, { refreshCells: true, redrawRows: true });

          showSuccessNotification(
            { message: t('messageCalculatePricingSuccess') },
            NotificationPosition.TOP
          );
        },
      });
    }, 1100);
  };

  const handleOnSubmitSuccess = (importData) => {
    if (!importData?.partData?.length) {
      return;
    }

    calculatePricingAction(importData);

    onSubmitSuccess?.();
  };

  return (
    <BomPartsImportProvider
      createImport={createQuoteImport}
      createImportPayload={createImportPayload}
      type={type}
      onSubmitSuccess={handleOnSubmitSuccess}
      isAlternateVersionEnabled={Boolean(quote?.alternateVersionEnabled)}
      attachmentType={FileUploadType.BOM_IMPORT}
    >
      <BomPartsImport isOpen={isOpen} onClose={onClose} fileId={fileId} />
    </BomPartsImportProvider>
  );
};
